// The basic CPU: greedy, never offers trades. Kept as a baseline for the
// smart one (see `npm run arena`).

import { TERRAIN_RESOURCE } from './board';
import { legalActions } from './game';
import { COSTS, RESOURCES, hasResources, totalResources, type Resource, type Resources } from './resources';
import { bankRate, canPlaceSettlement, discardOwed, suggestDiscard, victoryPoints } from './rules';
import { topologyOf } from './topology';
import { best, missing, pips, production, robberOnMe } from './bot-common';
import type { Action, GameState } from './types';

/** How good a spot is for `player`: total pips, with a bonus for resources they don't produce yet and for harbors. */
function vertexValue(state: GameState, player: number, v: number): number {
  const t = topologyOf(state);
  const have = production(state, player);
  let score = 0;
  for (const h of t.vertices[v]!.hexes) {
    const tile = state.board.tiles[h]!;
    const r = TERRAIN_RESOURCE[tile.terrain];
    if (!r) continue;
    const p = pips(tile.token);
    score += p + (have[r] === 0 ? p * 0.6 : 0) + (r === 'ore' || r === 'grain' ? p * 0.15 : 0);
  }
  for (const port of state.board.ports) {
    if (!t.edges[port.edge]!.vertices.includes(v)) continue;
    score += port.type === 'any' ? 1.5 : have[port.type] >= 5 ? 3 : 1;
  }
  return score;
}

/** Best spot a road along `edge` opens up (its far end, or one step past it). */
function roadValue(state: GameState, player: number, edge: number): number {
  const t = topologyOf(state);
  let best = 0;
  for (const end of t.edges[edge]!.vertices) {
    if (state.buildings[end]?.owner === player) continue;
    for (const v of [end, ...t.vertices[end]!.neighbors]) {
      if (state.buildings[v] || t.vertices[v]!.neighbors.some((n) => state.buildings[n])) continue;
      best = Math.max(best, vertexValue(state, player, v) * (v === end ? 1 : 0.8));
    }
  }
  return best;
}

type Goal = 'city' | 'settlement' | 'devCard' | 'road';

/** What the bot is saving for, most valuable first among what's possible at all. */
function goals(state: GameState, player: number): Goal[] {
  const p = state.players[player]!;
  const t = topologyOf(state);
  const out: Goal[] = [];
  if (p.citiesLeft > 0 && state.buildings.some((b) => b?.owner === player && b.kind === 'settlement')) out.push('city');
  const spot = p.settlementsLeft > 0 && t.vertices.some((_, v) => canPlaceSettlement(state, player, v, false));
  if (spot) out.push('settlement');
  if (state.devDeck.length > 0) out.push('devCard');
  if (!spot && p.roadsLeft > 0) out.push('road');
  return out;
}

/** The next action for `player`, or null if it's not their move. */
export function basicAction(state: GameState, player: number): Action | null {
  const phase = state.phase;
  if (phase.kind === 'gameOver') return null;

  const owed = discardOwed(state, player);
  if (owed > 0) return { type: 'discard', resources: suggestDiscard(state.players[player]!.resources, owed) };

  if (phase.kind === 'main' && state.trade && state.trade.from !== player && state.trade.responses[player] === null) {
    return { type: 'respondTrade', accept: wantsTrade(state, player, state.trade.give, state.trade.want) };
  }

  if (player !== state.currentPlayer) return null;
  const legal = legalActions(state, player);
  if (legal.length === 0) return null;
  const of = <K extends Action['type']>(type: K) => legal.filter((a): a is Extract<Action, { type: K }> => a.type === type);
  const hand = state.players[player]!.resources;

  switch (phase.kind) {
    case 'setup': {
      if (phase.step === 'settlement') return best(of('buildSettlement'), (a) => vertexValue(state, player, a.vertex)) ?? null;
      return best(of('buildRoad'), (a) => roadValue(state, player, a.edge)) ?? null;
    }
    case 'roadBuilding':
      return best(of('buildRoad'), (a) => roadValue(state, player, a.edge)) ?? null;
    case 'moveRobber':
      return best(of('moveRobber'), (a) => robberValue(state, player, a.hex)) ?? null;
    case 'steal': {
      const victim = best(phase.candidates, (c) => totalResources(state.players[c]!.resources) * 10 + victoryPoints(state, c));
      return victim === undefined ? null : { type: 'steal', victim };
    }
    case 'preRoll': {
      const knight = of('playKnight')[0];
      if (knight && robberOnMe(state, player)) return knight;
      return of('rollDice')[0] ?? null;
    }
    case 'discard':
      return null;
    case 'main':
      break;
  }

  // Main phase. An open offer is only ever ours here (bots don't offer), so withdraw it.
  if (state.trade?.from === player) return { type: 'cancelTrade' };

  const knight = of('playKnight')[0];
  if (knight && robberOnMe(state, player)) return knight;

  const city = best(of('buildCity'), (a) => vertexValue(state, player, a.vertex));
  if (city) return city;
  const settlement = best(of('buildSettlement'), (a) => vertexValue(state, player, a.vertex));
  if (settlement) return settlement;

  const plan = goals(state, player);
  const goal = plan[0];
  const need = goal ? missing(hand, COSTS[goal]) : null;

  // Year of Plenty for whatever the top goal is short of.
  const plenty = of('playYearOfPlenty');
  if (need && plenty.length > 0 && totalResources(need) > 0) {
    const wanted = RESOURCES.filter((r) => need[r] > 0);
    const pickA = wanted[0]!;
    const pickB = need[pickA] > 1 ? pickA : (wanted[1] ?? pickA);
    const match = plenty.find((a) => a.resources.includes(pickA) && a.resources.includes(pickB) && (pickA !== pickB || a.resources[0] === a.resources[1]));
    if (match) return match;
  }
  const monopoly = of('playMonopoly');
  if (need && monopoly.length > 0 && totalResources(need) > 0) {
    const r = best(RESOURCES.filter((x) => need[x] > 0), (x) => need[x]);
    if (r) return monopoly.find((a) => a.resource === r) ?? null;
  }

  // Trade with the bank when that completes the goal without spending what the goal needs.
  if (goal && need && totalResources(need) > 0 && totalResources(need) <= 2) {
    const cost = COSTS[goal];
    const receive = RESOURCES.find((r) => need[r] > 0)!;
    const give = best(
      RESOURCES.filter((r) => r !== receive && hand[r] - cost[r] >= bankRate(state, player, r)),
      (r) => hand[r] - cost[r] - bankRate(state, player, r),
    );
    if (give && of('bankTrade').some((a) => a.give === give && a.receive === receive)) {
      return { type: 'bankTrade', give, receive };
    }
  }

  if (goal === 'devCard' || (plan.includes('devCard') && hasResources(hand, COSTS.devCard) && !plan.includes('settlement'))) {
    const buy = of('buyDevCard')[0];
    if (buy) return buy;
  }
  const roadCard = of('playRoadBuilding')[0];
  if (roadCard && plan.includes('road')) return roadCard;
  if (plan.includes('road')) {
    const road = best(of('buildRoad'), (a) => roadValue(state, player, a.edge));
    if (road && roadValue(state, player, road.edge) > 0) return road;
  }
  // Nothing to build: still use a 7-proof hand rather than sit on it.
  if (totalResources(hand) > 7) {
    const buy = of('buyDevCard')[0];
    if (buy) return buy;
    const road = best(of('buildRoad'), (a) => roadValue(state, player, a.edge));
    if (road) return road;
  }
  return of('endTurn')[0] ?? null;
}

/** Hurt the leader most, never ourselves. */
function robberValue(state: GameState, player: number, hex: number): number {
  const t = topologyOf(state);
  const p = pips(state.board.tiles[hex]!.token);
  let score = 0;
  for (const v of t.hexVertices[hex]!) {
    const b = state.buildings[v];
    if (!b) continue;
    if (b.owner === player) return -100;
    const weight = (b.kind === 'city' ? 2 : 1) * (1 + victoryPoints(state, b.owner) / 5);
    score += p * weight + (totalResources(state.players[b.owner]!.resources) > 0 ? 1 : 0);
  }
  return score;
}

/** Accept if we can pay, we get at least as many cards, and it helps what we're building. */
function wantsTrade(state: GameState, player: number, theyGive: Resources, theyWant: Resources): boolean {
  const hand = state.players[player]!.resources;
  if (!hasResources(hand, theyWant)) return false;
  if (totalResources(theyGive) < totalResources(theyWant)) return false;
  const goal = goals(state, player)[0];
  if (!goal) return false;
  const cost = COSTS[goal];
  const need = missing(hand, cost);
  const helps = RESOURCES.some((r: Resource) => theyGive[r] > 0 && need[r] > 0);
  const hurts = RESOURCES.some((r: Resource) => theyWant[r] > 0 && hand[r] - theyWant[r] < cost[r]);
  return helps && !hurts;
}
